// @mui
import { EventContentArg } from '@fullcalendar/core';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { GetVisitByIdVisitTypeEnum } from 'woofwoof-api';
import { mapVisitTypeToLabel } from '../../utils/mapVisitTypeToLabel';
import Iconify from '../iconify';

type CalendarEventProps = {
  event: EventContentArg;
};

export default function CalendarEvent({ event }: CalendarEventProps) {
  const { t } = useTranslation();
  const visit = event.event.extendedProps.visit;
  const timeOff = event.event.extendedProps.timeOff;
  const viewTypeMonth = event.view.type === 'dayGridMonth';
  const viewTypeDay = event.view.type === 'timeGridDay';
  const isInThePast = new Date(visit?.endTime) < new Date();

  if (timeOff) {
    const eventStart = event.event.start;
    const eventEnd = event.event.end;
    const durationMinutes =
      eventStart && eventEnd
        ? (eventEnd.getTime() - eventStart.getTime()) / (1000 * 60)
        : 0;
    const isInThePast = eventEnd ? eventEnd < new Date() : false;
    const color = isInThePast ? 'text.primary' : 'error.dark';
    const fontWeight = isInThePast ? 'normal' : 'bold';
    const isLargeEnoughForDescription = durationMinutes >= 40;
    const descriptionLineClamp = Math.max(
      1,
      Math.floor((durationMinutes - 30) / 10),
    );

    if (viewTypeMonth) {
      return (
        <Stack flexDirection="row" gap={0.5} alignItems="center">
          <Iconify
            color={color}
            icon="mdi:calendar-remove"
            width={16}
            sx={{ flexShrink: 0 }}
          />
          {timeOff.professionalName && (
            <Typography
              component="span"
              color={color}
              variant="body2"
              fontWeight={fontWeight}
              noWrap
            >
              {timeOff.professionalName}
            </Typography>
          )}
        </Stack>
      );
    }

    return (
      <Stack gap={0.25}>
        <Stack flexDirection="row" gap={0.5} alignItems="center">
          <Iconify
            color={color}
            icon="mdi:calendar-remove"
            width={16}
            sx={{ flexShrink: 0 }}
          />
          <Typography
            component="span"
            color={color}
            variant="body2"
            fontWeight={fontWeight}
            noWrap
          >
            {t('calendar.eventUnavailable')}
          </Typography>
        </Stack>
        {timeOff.professionalName && (
          <Typography
            component="span"
            color={color}
            variant="body2"
            fontWeight={fontWeight}
            noWrap
          >
            {timeOff.professionalName}
          </Typography>
        )}
        <Typography
          component="span"
          color={color}
          variant="body2"
          lineHeight="22px"
          fontSize="12px"
        >
          {event.timeText}
        </Typography>
        {timeOff.description && (
          <Typography
            component="span"
            color={color}
            variant="body2"
            fontSize="11px"
            lineHeight="16px"
            sx={{
              opacity: 0.8,
              wordBreak: 'break-word',
              whiteSpace: 'normal',
              overflowWrap: 'anywhere',
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: isLargeEnoughForDescription
                ? descriptionLineClamp
                : 1,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {timeOff.description}
          </Typography>
        )}
      </Stack>
    );
  }

  const getColorByVisitType = () => {
    if (isInThePast) {
      return 'text.primary';
    }
    if (visit.visitType === GetVisitByIdVisitTypeEnum.Onsite) {
      return 'primary.dark';
    }
    if (visit.visitType === GetVisitByIdVisitTypeEnum.Video) {
      return 'warning.dark';
    }
    if (visit.visitType === GetVisitByIdVisitTypeEnum.Chat) {
      return 'error.dark';
    }
    if (visit.visitType === GetVisitByIdVisitTypeEnum.Mobile) {
      return 'success.dark';
    }
    return 'text.primary';
  };

  const getVisitTypeBadge = () => {
    if (visit.visitType === GetVisitByIdVisitTypeEnum.Onsite) {
      return (
        <Stack flexDirection="row" gap={0.5} alignItems="center">
          <Iconify
            color={getColorByVisitType()}
            icon="solar:home-add-bold"
            width={20}
            sx={{ flexShrink: 0 }}
          />
          <Typography
            component="span"
            color={getColorByVisitType()}
            variant="body2"
            fontWeight={isInThePast ? 'normal' : 'bold'}
            noWrap
          >
            {mapVisitTypeToLabel(visit.visitType)}
          </Typography>
        </Stack>
      );
    }

    if (visit.visitType === GetVisitByIdVisitTypeEnum.Video) {
      return (
        <Stack flexDirection="row" gap={0.5} alignItems="center">
          <Iconify
            color={getColorByVisitType()}
            icon="solar:videocamera-record-bold"
            width={20}
            sx={{ flexShrink: 0 }}
          />
          <Typography
            component="span"
            color={getColorByVisitType()}
            variant="body2"
            fontWeight={isInThePast ? 'normal' : 'bold'}
            noWrap
          >
            {mapVisitTypeToLabel(visit.visitType)}
          </Typography>
        </Stack>
      );
    }

    if (visit.visitType === GetVisitByIdVisitTypeEnum.Chat) {
      return (
        <Stack flexDirection="row" gap={0.5} alignItems="center">
          <Iconify
            color={getColorByVisitType()}
            icon="solar:chat-dots-bold"
            width={20}
            sx={{ flexShrink: 0 }}
          />
          <Typography
            component="span"
            color={getColorByVisitType()}
            variant="body2"
            fontWeight={isInThePast ? 'normal' : 'bold'}
            noWrap
          >
            {mapVisitTypeToLabel(visit.visitType)}
          </Typography>
        </Stack>
      );
    }

    if (visit.visitType === GetVisitByIdVisitTypeEnum.Mobile) {
      return (
        <Stack flexDirection="row" gap={0.5} alignItems="center">
          <Iconify
            color={getColorByVisitType()}
            icon="solar:map-point-hospital-bold"
            width={20}
            sx={{ flexShrink: 0 }}
          />
          <Typography
            component="span"
            color={getColorByVisitType()}
            variant="body2"
            fontWeight={isInThePast ? 'normal' : 'bold'}
            noWrap
          >
            {mapVisitTypeToLabel(visit.visitType)}
          </Typography>
        </Stack>
      );
    }
    return null;
  };

  if (viewTypeMonth) {
    return (
      <>
        {getVisitTypeBadge()}
        <Stack flexDirection="row" gap={1} alignItems="center">
          <Typography
            component="span"
            color={getColorByVisitType()}
            variant="body2"
            fontWeight={isInThePast ? 'normal' : 'bold'}
          >
            {format(new Date(visit.startTime), 'H:mm')}
          </Typography>
          <Typography
            component="span"
            fontWeight={isInThePast ? 'normal' : 'bold'}
            color={getColorByVisitType()}
            variant="body2"
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
          >
            {visit.profile?.name}
          </Typography>
        </Stack>
      </>
    );
  }

  if (viewTypeDay) {
    return (
      <>
        {getVisitTypeBadge()}
        <Stack>
          <Typography
            component="span"
            fontWeight={isInThePast ? 'normal' : 'bold'}
            color={getColorByVisitType()}
            variant="body2"
          >
            {visit.profile?.name}
          </Typography>
          <Typography
            component="span"
            fontWeight={isInThePast ? 'normal' : 'bold'}
            color={getColorByVisitType()}
            variant="body2"
            lineHeight="22px"
            fontSize="12px"
          >
            {`${event.timeText} z ${visit.user?.firstName} ${visit.user?.lastName}`}
          </Typography>
        </Stack>
      </>
    );
  }

  return (
    <>
      {getVisitTypeBadge()}
      <Stack>
        <Typography
          component="span"
          fontWeight={isInThePast ? 'normal' : 'bold'}
          color={getColorByVisitType()}
          variant="body2"
        >
          {visit.profile?.name}
        </Typography>
        <Typography
          component="span"
          fontWeight={isInThePast ? 'normal' : 'bold'}
          color={getColorByVisitType()}
          variant="body2"
          lineHeight="22px"
          fontSize="12px"
        >
          {event.timeText}
        </Typography>
      </Stack>
    </>
  );
}
